<script setup lang="ts">
import { ref } from 'vue'
import { Button, Segmented, Space, Typography, Divider, Card } from 'ant-design-vue'
import Editor from './components/Editor.vue'
import type { EditorMode, EditorType } from './core/editor-factory'

type EditorTypeStrict = EditorType

const mode = ref<EditorMode>('edit')
const editorType = ref<EditorTypeStrict>('codemirror')
const theme = ref<'light' | 'dark'>('light')
const markdown = ref<string>(`# Markdown 编辑器

支持 **加粗**、*斜体*、~~删除线~~、\`行内代码\` 以及 ==高亮==。

## 1. 列表与标题

- 无序列表项
- 嵌套列表
1. 有序子项
2. 有序子项

### 任务列表

- [x] 已完成任务
- [ ] 待办任务

## 2. 代码高亮

\`\`\`typescript
interface User {
  id: number
  name: string
  email: string
}

function greet(user: User): string {
  return \`Hello, \${user.name}!\`
}
\`\`\`

## 3. 表格

| 功能 | 支持 |
| --- | --- |
| Markdown | ✔ |
| MathJax 数学 | ✔ |
| Typst | ✔ |

## 4. 引用

> 这是一个引用块，用于强调重要内容。

## 5. 数学公式

行内公式：$E = mc^2$

块级公式：

\`\`\`math
\\int_0^\\infty e^{-x^2}\\,dx = \\frac{\\sqrt{\\pi}}{2}
\`\`\`

## 6. Typst 渲染

\`\`\`typst
#set page(width: auto, height: auto, margin: 1em)
#set text(font: "New Computer Modern")

#align(center)[#text(size: 2em, weight: "bold")[Typst 公式]]

#align(center)[
  $ integral_0^infinity e^(-x^2) dif x = sqrt(pi) / 2 $
]
\`\`\`

## 7. 脚注

这是一个脚注示例[^1]。

[^1]: 这是脚注内容。
`)

function setMode(m: string | number) {
  mode.value = m as EditorMode
}

function setEditorType(t: string | number) {
  editorType.value = t as EditorTypeStrict
}

function setTheme(t: string | number) {
  theme.value = t as 'light' | 'dark'
  document.documentElement.setAttribute('data-theme', theme.value)
}
</script>

<template>
  <div class="app" :data-theme="theme">
    <div class="app__header">
      <div class="app__title">
        <h1>Markdown 编辑器</h1>
        <span class="app__subtitle">markdown-it + shiki + MathJax + typst</span>
      </div>
      <Space>
        <Space direction="vertical" :size="4">
          <span class="app__label">模式</span>
          <Segmented
            :value="mode"
            :options="[{ label: '编辑', value: 'edit' }, { label: '渲染', value: 'render' }]"
            @change="setMode"
          />
        </Space>
        <Space direction="vertical" :size="4">
          <span class="app__label">编辑器</span>
          <Segmented
            :value="editorType"
            :options="[
              { label: 'CodeMirror', value: 'codemirror' },
              { label: 'Monaco', value: 'monaco' },
              { label: 'Ace', value: 'ace' },
            ]"
            @change="setEditorType"
          />
        </Space>
        <Space direction="vertical" :size="4">
          <span class="app__label">主题</span>
          <Segmented
            :value="theme"
            :options="[{ label: '亮', value: 'light' }, { label: '暗', value: 'dark' }]"
            @change="setTheme"
          />
        </Space>
      </Space>
    </div>

    <div class="app__body">
      <Editor
        v-model="markdown"
        :mode="mode"
        :editor-type="editorType"
        :theme="theme"
        height="100%"
        placeholder="在这里输入 Markdown..."
      />
    </div>
  </div>
</template>

<style scoped>
.app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 0;
  background: var(--color-bg-secondary);
  color: var(--color-text);
}

.app__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  background: var(--color-bg);
  border-bottom: 1px solid var(--color-border);
}

.app__title h1 {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.app__subtitle {
  font-size: 12px;
  color: var(--color-text-tertiary);
}

.app__label {
  font-size: 11px;
  font-weight: 500;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.app__body {
  flex: 1;
  min-height: 0;
  padding: 16px;
}
</style>